import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "tr" | "en";

type Dict = Record<string, { tr: string; en: string }>;

const dict: Dict = {
  "nav.home": { tr: "Anasayfa", en: "Home" },
  "nav.about": { tr: "Hakkımızda", en: "About" },
  "nav.services": { tr: "Hizmetlerimiz", en: "Services" },
  "nav.announcements": { tr: "Duyurular", en: "Announcements" },
  "nav.contact": { tr: "İletişim", en: "Contact" },
  "common.details": { tr: "Detaylar", en: "Details" },
  "common.readMore": { tr: "Devamını Oku", en: "Read More" },
  "common.send": { tr: "Gönder", en: "Send" },
  "common.name": { tr: "Adınız Soyadınız", en: "Full Name" },
  "common.email": { tr: "E-posta", en: "Email" },
  "common.phone": { tr: "Telefon", en: "Phone" },
  "common.subject": { tr: "Konu", en: "Subject" },
  "common.message": { tr: "Mesajınız", en: "Your Message" },
  "common.address": { tr: "Adres", en: "Address" },
  "common.hours": { tr: "Çalışma Saatleri", en: "Working Hours" },
  "common.followUs": { tr: "Doğru Bilgiye Yabancı Kalmayın...", en: "Don't stay away from the right information..." },
  "common.allRights": { tr: "Tüm hakları saklıdır.", en: "All rights reserved." },
  "section.services": { tr: "Hizmetlerimiz", en: "Our Services" },
  "section.whyUs": { tr: "Neden Biz?", en: "Why Us?" },
  "section.contact": { tr: "Bize Ulaşın", en: "Get In Touch" },
  "section.announcements": { tr: "Duyurular", en: "Announcements" },
  "stats.customers": { tr: "Müşteri", en: "Clients" },
  "stats.applications": { tr: "Başvuru", en: "Applications" },
  "stats.countries": { tr: "Ülke", en: "Countries" },
  "form.success": { tr: "Mesajınız gönderildi. Teşekkürler!", en: "Your message has been sent. Thank you!" },
  "form.error": { tr: "Bir hata oluştu, lütfen tekrar deneyin.", en: "An error occurred, please try again." },
  "admin.title": { tr: "Yönetim Paneli", en: "Admin Panel" },
  "admin.login": { tr: "Giriş Yap", en: "Sign In" },
  "admin.logout": { tr: "Çıkış", en: "Logout" },
};

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (k: keyof typeof dict | string) => string;
};

const LangCtx = createContext<Ctx | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("tr");

  useEffect(() => {
    const saved = (typeof window !== "undefined" && localStorage.getItem("lang")) as Lang | null;
    if (saved === "tr" || saved === "en") setLangState(saved);
  }, []);

  const setLang = (l: Lang) => {
    setLangState(l);
    if (typeof window !== "undefined") localStorage.setItem("lang", l);
  };

  const t = (k: string) => dict[k]?.[lang] ?? k;

  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export function useLang() {
  const ctx = useContext(LangCtx);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}

export function pick<T extends Record<string, any>>(row: T, base: string, lang: Lang): string {
  return row[`${base}_${lang}`] ?? row[`${base}_tr`] ?? "";
}
